import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { gsap } from 'gsap'
gsap.registerPlugin(ScrollTrigger)

export default class SmoothScroll {
  constructor(opts = {}) {
    this.wrapper        = opts.wrapper   || document.getElementById('ss-wrapper');
    this.content        = opts.content   || document.getElementById('ss-content');
    this.smooth         = opts.smooth ?? 0.12;             // 慣性粘度（0.08〜0.2）
    this.tweenEase      = opts.tweenEase ?? 'power2.out';  // アンカー移動のイーズ
    this.tweenDuration  = opts.tweenDuration ?? 0.6;       // アンカー移動の時間
    this.offsetVar      = opts.offsetVar || '--ss-offset'; // :root公開（px）
    this.progressVar    = opts.progressVar || '--ss-progress'; // :root公開（0..1）
    this.hashBehavior   = opts.hashBehavior || 'push';     // 'push' | 'replace' | 'none'
    this.offset         = opts.offset || 0;                // 固定ヘッダ分など

    this._onRaf         = this._onRaf.bind(this);
    this._onResize      = this._onResize.bind(this);
    this._onClick       = this._onClick.bind(this);
    this._onHashChange  = this._onHashChange.bind(this);

    this.rafId = null;
    this.enabled = false;
    this.targetY = 0;
    this.currentY = 0;
    this.docHeight = 0;
    this.maxScroll = 0;

    this._initialHash = null;
    this._didInitHash = false;

    this.hasScrollTo = !!(gsap && gsap.plugins && gsap.plugins.ScrollToPlugin);
  }

  init() {
    // ブラウザの復帰スクロール/初期ハッシュジャンプを制御
    try { if ('scrollRestoration' in history) history.scrollRestoration = 'manual'; } catch(e){}
    if (location.hash && location.hash.length > 1) {
      this._initialHash = location.hash.substring(1);
      window.scrollTo(0, 0); // まずネイティブジャンプを打消す
    }

    this._measure();
    window.addEventListener('resize', this._onResize, {passive:true});
    document.addEventListener('click', this._onClick);
    window.addEventListener('hashchange', this._onHashChange);

    this.targetY = window.pageYOffset || document.documentElement.scrollTop || 0;
    this.currentY = this._clamp(this.targetY);

    this.enabled = true;
    this._onRaf();

    // 計測＆描画開始後に初期ハッシュへ自前でジャンプ（2フレーム遅延で安定）
    if (this._initialHash && !this._didInitHash) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.scrollToHash(this._initialHash, { immediate: true, updateHash: false });
          this._didInitHash = true;
        });
      });
    }
  }

  reset(){ this._measure(); }

  destroy() {
    this.enabled = false;
    if (this.rafId) cancelAnimationFrame(this.rafId);
    window.removeEventListener('resize', this._onResize);
    document.removeEventListener('click', this._onClick);
    window.removeEventListener('hashchange', this._onHashChange);
    if (this.wrapper) { this.wrapper.style.position = ''; this.wrapper.style.inset = ''; }
    if (this.content) this.content.style.transform = '';
    const root = document.documentElement;
    root.style.removeProperty(this.offsetVar);
    root.style.removeProperty(this.progressVar);
    document.body.style.height = '';
  }

  _measure() {
    if (!this.content) return;
    const h = this.content.scrollHeight;
    if (h !== this.docHeight) {
      this.docHeight = h;
      document.body.style.height = h + 'px';
    }
    this.maxScroll = Math.max(0, this.docHeight - window.innerHeight);
    // はみ出し補正
    this.currentY = this._clamp(this.currentY);
    this.targetY  = this._clamp(this.targetY);
  }

  _clamp(y){ return Math.max(0, Math.min(this.maxScroll, y|0)); }

  _onResize() {
    this._measure();
    if (this.currentY > this.maxScroll) {
      this.currentY = this.targetY = this.maxScroll;
      if (this.content) this.content.style.transform = 'translate3d(0,' + (-this.currentY) + 'px,0)';
      window.scrollTo(0, this.currentY);
    }
  }

  _onRaf() {
    if (!this.enabled) return;

    this.targetY = this._clamp(window.pageYOffset || document.documentElement.scrollTop || 0);
    this.currentY += (this.targetY - this.currentY) * this.smooth;
    this.currentY = this._clamp(this.currentY);

    if (this.content) {
      this.content.style.transform = 'translate3d(0,' + (-this.currentY) + 'px,0)';
    }

    const root = document.documentElement;
    root.style.setProperty(this.offsetVar, this.currentY.toFixed(3) + 'px');
    const max = Math.max(1, this.maxScroll);
    root.style.setProperty(this.progressVar, (this.currentY / max).toFixed(6));

    const ro = document.getElementById('readout');
    if (ro) ro.textContent = Math.round(this.currentY);

    this.rafId = requestAnimationFrame(this._onRaf);
  }

  _onClick(e) {
    const a = e.target.closest && e.target.closest('a[href^="#"]');
    if (!a) return;
    const href = a.getAttribute('href');
    if (!href || href === '#') return;
    const id = href.slice(1);
    const target = document.getElementById(id);
    if (!target) return;
    e.preventDefault();
    this.scrollToEl(target, { updateHash: true });
  }

  _onHashChange() {
    if (!location.hash || location.hash.length <= 1) return;
    const id = location.hash.substring(1);
    const el = document.getElementById(id);
    if (!el) return;
    this.scrollToEl(el, { updateHash: false });
  }

  scrollToHash(id, opts={}) {
    const el = document.getElementById(id);
    if (!el) return;
    this.scrollToEl(el, opts);
  }

  scrollToEl(el, { duration, offset = this.offset, updateHash = false, immediate = false } = {}) {
    const rect = el.getBoundingClientRect();
    const base = (window.pageYOffset || document.documentElement.scrollTop || 0);
    let y = base + rect.top + offset;
    y = this._clamp(y);

    if (immediate) {
      window.scrollTo(0, y);
      this.currentY = this.targetY = y;
      return;
    }

    if (this.hasScrollTo) {
      gsap.to(window, {
        scrollTo: y,
        duration: duration ?? this.tweenDuration,
        ease: this.tweenEase,
        onComplete: () => {
          if (updateHash) {
            if (this.hashBehavior === 'push') history.pushState(null, '', '#' + el.id);
            else if (this.hashBehavior === 'replace') history.replaceState(null, '', '#' + el.id);
          }
        }
      });
    } else {
      const obj = { v: base };
      gsap.to(obj, {
        v: y,
        duration: duration ?? this.tweenDuration,
        ease: this.tweenEase,
        onUpdate: () => window.scrollTo(0, obj.v),
        onComplete: () => {
          if (updateHash) {
            if (this.hashBehavior === 'push') history.pushState(null, '', '#' + el.id);
            else if (this.hashBehavior === 'replace') history.replaceState(null, '', '#' + el.id);
          }
        }
      });
    }
  }
}
